/**
 * God Developer API Service
 * 
 * Connects to the God Factory for app building and code generation.
 * Powers the Live Preview and Building Phase in CreateAppScreen.
 */
import { getIdToken } from './firebase';

const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
const GOD_DEVELOPER_URL = `${ORBIT_GATEWAY_URL}/api/god`;

// =============================================================================
// TYPES
// =============================================================================

export interface DevelopRequest {
  requirements: string;
  project_name?: string;
  context?: Record<string, unknown>;
  knowledge_base?: { type: string; name: string; url?: string }[];
}

export interface DevelopResponse {
  success: boolean;
  correlation_id: string;
  result?: {
    code?: string;
    files?: { path: string; content: string }[];
    features?: string[];
    components?: string[];
    preview_html?: string;
  };
  team_contributions?: Record<string, string>;
  verification?: {
    verified: boolean;
    confidence: number;
    warnings: string[];
  };
  error?: string;
}

export interface GenerateRequest {
  prompt: string;
  type: 'component' | 'feature' | 'style' | 'logic';
  context?: Record<string, unknown>;
}

export interface GenerateResponse {
  success: boolean;
  code: string;
  explanation?: string;
  model?: string;
}

export interface StreamChunk {
  type: 'thinking' | 'code' | 'preview' | 'feature' | 'status' | 'complete' | 'error';
  data: string;
  file_path?: string;
  component?: string;
  timestamp: string;
}

// =============================================================================
// HELPERS
// =============================================================================

async function getHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// =============================================================================
// GOD DEVELOPER API
// =============================================================================

/**
 * Verify requirements before building
 */
export async function verifyRequirements(requirements: string): Promise<{
  verified: boolean;
  confidence: number;
  warnings: string[];
  suggestions: string[];
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${GOD_DEVELOPER_URL}/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ requirements })
    });

    if (!response.ok) {
      throw new Error(`Verification failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[GodAPI] Verification error:', error);
    return {
      verified: false,
      confidence: 0,
      warnings: ['Verification service unavailable - backend confirmation failed'],
      suggestions: ['Retry when verification service is reachable before claiming the build is validated.']
    };
  }
}

/**
 * Start app development with the God Agent team
 */
export async function developApp(request: DevelopRequest): Promise<DevelopResponse> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${GOD_DEVELOPER_URL}/build`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        requirements: request.requirements,
        project_name: request.project_name || 'New App',
        context: request.context || {},
        knowledge_base: request.knowledge_base || []
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Development failed: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[GodAPI] Development error:', error);
    throw error;
  }
}

/**
 * Stream app development with real-time updates
 */
export async function developAppStream(
  request: DevelopRequest,
  onChunk: (chunk: StreamChunk) => void,
  onComplete: (result: DevelopResponse) => void,
  onError: (error: Error) => void
): Promise<void> {
  try {
    const headers = await getHeaders();
    
    const response = await fetch(`${GOD_DEVELOPER_URL}/build/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        requirements: request.requirements,
        project_name: request.project_name || 'New App',
        context: request.context || {},
        knowledge_base: request.knowledge_base || [],
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error(`Stream failed: ${response.status}`);
    }

    if (!response.body) {
      // Fallback to non-streaming
      const result = await response.json();
      onComplete(result);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const chunk = JSON.parse(data) as StreamChunk;
            if (chunk.type === 'complete') {
              onComplete(chunk.data as unknown as DevelopResponse);
            } else if (chunk.type === 'error') {
              onError(new Error(chunk.data));
            } else {
              onChunk(chunk);
            }
          } catch {
            onChunk({
              type: 'status',
              data,
              timestamp: new Date().toISOString()
            });
          }
        }
      }
    }
  } catch (error) {
    console.error('[GodAPI] Stream error:', error);
    onError(error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Generate a specific component or feature
 */
export async function generateCode(request: GenerateRequest): Promise<GenerateResponse> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${GOD_DEVELOPER_URL}/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(`Generation failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[GodAPI] Generation error:', error);
    throw error;
  }
}

/**
 * Execute code in sandbox and get preview
 */
export async function executePreview(code: string, type: 'react' | 'html' = 'react'): Promise<{
  success: boolean;
  preview_url?: string;
  html?: string;
  error?: string;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${GOD_DEVELOPER_URL}/forge/execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code, type })
    });

    if (!response.ok) {
      throw new Error(`Execution failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[GodAPI] Execution error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Execution failed'
    };
  }
}

/**
 * Get the God Developer team info
 */
export async function getTeam(): Promise<{
  team: { name: string; role: string; specialty: string }[];
}> {
  try {
    const response = await fetch(`${GOD_DEVELOPER_URL}/team`);
    if (!response.ok) {
      throw new Error(`Team fetch failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[GodAPI] Team fetch error:', error);
    return {
      team: [
        { name: 'Jasmine', role: 'Lead Designer', specialty: 'UX Architecture' },
        { name: 'Big Bill', role: 'Backend Architect', specialty: 'System Design' },
        { name: 'Bella', role: 'Frontend Dev', specialty: 'React/TypeScript' },
        { name: 'Leo', role: 'Data Integration', specialty: 'AI Enhancement' },
        { name: 'Dan', role: 'QA & Deploy', specialty: 'Testing & CI/CD' }
      ]
    };
  }
}

/**
 * Check God Developer service health
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${GOD_DEVELOPER_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}
